import { Component, Input, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';

import { LayoutStore } from '../../../store/layout.store';
import { UtilService } from '../../../core/services/util.service';
import { SidebarMenuItem, UserType } from '../models/private-sidebar.types';
import { IaAssistService } from '../../../features/shared/services/ia-assist.service';
import { LoadingService } from '../../../core/services/loading.service';
import { GenericDialogService } from '../../../core/services/generic-dialog.service';
import { HelperDialogComponent } from '../dialogs/helper/helper-dialog.component';
import { AppSnackbarService } from '../../../core/notifications/snackbar.service';
import { environment } from '../../../environments/environment';
import { PermissionService } from '../../../core/auth/permission.service';

@Component({
  selector: 'app-private-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  templateUrl: './private-sidebar.component.html',
  styleUrls: ['./private-sidebar.component.scss'],
})
export class PrivateSidebarComponent {
  @Input() menuItems: SidebarMenuItem[] = [];

  private readonly layoutStore = inject(LayoutStore);
  private readonly utilService = inject(UtilService);
  private readonly iaAssistService = inject(IaAssistService);
  private readonly loadingService = inject(LoadingService);
  private readonly genericDialog = inject(GenericDialogService);
  private readonly translate = inject(TranslateService);
  private readonly snackbarService = inject(AppSnackbarService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(PermissionService);

  readonly isExpanded = this.layoutStore.isSidebarExpanded;
  readonly isMobileOpen = this.layoutStore.isMobileSidebarOpen;

  readonly visibleMenuItems = computed(() =>
    this.filterMenuItems(this.menuItems),
  );

  tokenAI: string | null = null;

  private readonly openSubmenus = signal<Set<string>>(new Set());
  private readonly openSubSubmenus = signal<Set<string>>(new Set());
  private readonly openSubSubSubmenus = signal<Set<string>>(new Set());

  private filterMenuItems(items: SidebarMenuItem[]): SidebarMenuItem[] {
    return items
      .map((item) => {
        const children = item.children
          ? this.filterMenuItems(item.children)
          : undefined;

        return {
          ...item,
          children,
        };
      })
      .filter((item) => this.canShowItem(item));
  }

  private canShowItem(item: SidebarMenuItem): boolean {
    // Admin e Standard sempre veem
    if (
      this.permissionService.isAdmin() ||
      this.permissionService.isStandard()
    ) {
      return true;
    }

    // Perfis restritos só veem o que foi liberado
    if (!item.allowedShipperProfiles?.length) {
      return false;
    }
    
    return item.allowedShipperProfiles.some((profile) =>
      this.hasShipperProfile(profile),
    );
  }

  private hasShipperProfile(profile: UserType): boolean {
    const permissions: Record<UserType, boolean> = {
      admin: this.permissionService.isAdmin(),
      standard: this.permissionService.isStandard(),
      order: this.permissionService.isOrder(),
      requester: this.permissionService.isRequester(),
      approver: this.permissionService.isApprover(),
    };

    return permissions[profile];
  }

  menuId(id: string): string {
    return 'menu-' + this.utilService.removeAccentuationForID(id);
  }

  isSubmenuOpen(itemId: string): boolean {
    return this.openSubmenus().has(itemId);
  }

  toggleSubmenu(itemId: string): void {
    this.openSubmenus.update((set) => {
      const next = new Set(set);

      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }

      return next;
    });
  }

  isSubSubmenuOpen(itemId: string): boolean {
    return this.openSubSubmenus().has(itemId);
  }

  toggleSubSubmenu(itemId: string): void {
    this.openSubSubmenus.update((set) => {
      const next = new Set(set);

      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }

      return next;
    });
  }

  isSubSubSubmenuOpen(itemId: string): boolean {
    return this.openSubSubSubmenus().has(itemId);
  }

  toggleSubSubSubmenu(itemId: string): void {
    this.openSubSubSubmenus.update((set) => {
      const next = new Set(set);

      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }

      return next;
    });
  }

  toggleSidebar(): void {
    this.layoutStore.toggleSidebar();
  }

  closeOnMobile(): void {
    this.layoutStore.closeMobileSidebar();
  }

  closeMobileOverlay(): void {
    this.layoutStore.closeMobileSidebar();
  }

  onSaraClick(): void {
    this.closeOnMobile();

    if (!environment.VIEW_AI) {
      const helperData = {
        sections: [
          {
            type: 'hero',
            icon: 'smart_toy',
            title: 'iaassist.helper.not_available.title',
            subtitle: 'iaassist.helper.not_available.description',
            className: 'hero-card',
          },
        ],
        actions: [
          {
            label: 'iaassist.helper.ok',
            icon: 'check',
            closeOnClick: true,
          },
        ],
      } as any;

      this.genericDialog.open(
        {
          title: 'iaassist.helper.not_available.title',
          icon: 'help_outline',
          iconTheme: 'primary',
          buttons: [],
          showCloseButton: true,
          contentData: helperData,
          maxWidth: '560px',
        },
        HelperDialogComponent,
      );

      return;
    }

    let cached = localStorage.getItem('token_ia');

    if (cached) {
      try {
        const parsed = JSON.parse(cached);

        if (parsed?.access_token || parsed?.accessToken) {
          cached = parsed.access_token ?? parsed.accessToken;
        }
      } catch {
        // Mantém o token como string simples
      }
    }

    if (cached) {
      this.loadingService.show();

      this.iaAssistService
        .getInstance(cached)
        .pipe(finalize(() => this.loadingService.hide()))
        .subscribe({
          next: (inst: any) => {
            const status = Array.isArray(inst)
              ? inst?.[0]?.status
              : inst?.instances?.[0]?.status;

            const ready = status === 'COMPLETED';

            if (ready) {
              this.router.navigate(['/app/iaassist']);
              return;
            }

            const helperData = {
              sections: [
                {
                  type: 'hero',
                  icon: 'autorenew',
                  title: 'iaassist.helper.syncing.title',
                  subtitle: 'iaassist.helper.syncing.description',
                  className: 'hero-card',
                },
              ],
              actions: [
                {
                  label: 'iaassist.helper.ok',
                  icon: 'check',
                  closeOnClick: true,
                },
              ],
            } as any;

            this.genericDialog.open(
              {
                title: 'iaassist.helper.syncing.title',
                icon: 'help_outline',
                contentData: helperData,
                showCloseButton: true,
                buttons: [],
              },
              HelperDialogComponent,
            );
          },
          error: (err: any) => {
            if (err?.status === 401) {
              const helperData = {
                sections: [
                  {
                    type: 'hero',
                    icon: 'error',
                    title: 'iaassist.helper.auth_error.title',
                    subtitle: 'iaassist.helper.auth_error.description',
                    className: 'hero-card',
                  },
                ],
                actions: [
                  {
                    label: 'iaassist.helper.ok',
                    icon: 'check',
                    closeOnClick: true,
                  },
                ],
              } as any;

              this.genericDialog.open(
                {
                  title: 'iaassist.helper.auth_error.title',
                  icon: 'error',
                  contentData: helperData,
                  showCloseButton: true,
                  buttons: [],
                },
                HelperDialogComponent,
              );
            } else {
              this.snackbarService.error(
                this.translate.instant('errors.http.server_error'),
              );
            }
          },
        });

      return;
    }

    this.loadingService.show();

    this.iaAssistService
      .authenticationIa()
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe({
        next: (resp: any) => {
          const token =
            resp?.jsonToken ??
            resp?.token ??
            resp?.access_token ??
            resp?.accessToken ??
            resp?.data?.token;

          if (!token || typeof token !== 'string') {
            const helperData = {
              sections: [
                {
                  type: 'hero',
                  icon: 'error',
                  title: 'iaassist.helper.auth_error.title',
                  subtitle: 'iaassist.helper.auth_error.description',
                  className: 'hero-card',
                },
              ],
              actions: [
                {
                  label: 'iaassist.helper.ok',
                  icon: 'check',
                  closeOnClick: true,
                },
              ],
            } as any;

            this.genericDialog.open(
              {
                title: 'iaassist.helper.auth_error.title',
                icon: 'error',
                contentData: helperData,
                showCloseButton: true,
                buttons: [],
              },
              HelperDialogComponent,
            );

            return;
          }

          let tokenToStore = token;

          try {
            const parsed = JSON.parse(token);

            if (parsed?.access_token || parsed?.accessToken) {
              tokenToStore = parsed.access_token ?? parsed.accessToken;
            }
          } catch {
            // Mantém o token como string simples
          }

          try {
            localStorage.setItem('token_ia', tokenToStore);
            this.tokenAI = tokenToStore;
          } catch (e) {
            console.warn('Unable to persist IA token locally', e);
            this.tokenAI = tokenToStore;
          }

          this.loadingService.show();

          this.iaAssistService
            .getInstance(tokenToStore)
            .pipe(finalize(() => this.loadingService.hide()))
            .subscribe({
              next: (inst: any) => {
                const status = Array.isArray(inst)
                  ? inst?.[0]?.status
                  : inst?.instances?.[0]?.status;

                const ready = status === 'COMPLETED';

                if (ready) {
                  this.router.navigate(['/app/iaassist']);
                  return;
                }

                const helperData = {
                  sections: [
                    {
                      type: 'hero',
                      icon: 'autorenew',
                      title: 'iaassist.helper.syncing.title',
                      subtitle: 'iaassist.helper.syncing.description',
                      className: 'hero-card',
                    },
                  ],
                  actions: [
                    {
                      label: 'iaassist.helper.ok',
                      icon: 'check',
                      closeOnClick: true,
                    },
                  ],
                } as any;

                this.genericDialog.open(
                  {
                    title: 'iaassist.helper.syncing.title',
                    icon: 'help_outline',
                    contentData: helperData,
                    showCloseButton: true,
                    buttons: [],
                  },
                  HelperDialogComponent,
                );
              },
              error: (err: any) => {
                if (err?.status === 401) {
                  const helperData = {
                    sections: [
                      {
                        type: 'hero',
                        icon: 'error',
                        title: 'iaassist.helper.auth_error.title',
                        subtitle: 'iaassist.helper.auth_error.description',
                        className: 'hero-card',
                      },
                    ],
                    actions: [
                      {
                        label: 'iaassist.helper.ok',
                        icon: 'check',
                        closeOnClick: true,
                      },
                    ],
                  } as any;

                  this.genericDialog.open(
                    {
                      title: 'iaassist.helper.auth_error.title',
                      icon: 'error',
                      contentData: helperData,
                      showCloseButton: true,
                      buttons: [],
                    },
                    HelperDialogComponent,
                  );
                } else {
                  this.snackbarService.error(
                    this.translate.instant('errors.http.server_error'),
                  );
                }
              },
            });
        },
        error: (err: any) => {
          if (err?.status === 400) {
            const helperData = {
              sections: [
                {
                  type: 'hero',
                  icon: 'hub',
                  title: 'iaassist.helper.no_integration.title',
                  subtitle: 'iaassist.helper.no_integration.description',
                  className: 'hero-card',
                },
              ],
              actions: [
                {
                  label: 'iaassist.helper.ok',
                  icon: 'check',
                  closeOnClick: true,
                },
              ],
            } as any;

            this.genericDialog.open(
              {
                title: 'iaassist.helper.no_integration.title',
                icon: 'hub',
                iconTheme: 'primary',
                buttons: [],
                showCloseButton: true,
                contentData: helperData,
                maxWidth: '560px',
              },
              HelperDialogComponent,
            );
          } else {
            const backendMsg =
              err?.error?.errorMessage ?? err?.error?.message ?? null;

            const message =
              backendMsg ?? this.translate.instant('errors.http.server_error');

            this.snackbarService.error(message);
          }
        },
      });
  }
}

// import { Component, Input, inject, signal } from '@angular/core';
// import { CommonModule } from '@angular/common';
// import { RouterModule, Router } from '@angular/router';
// import { TranslateModule, TranslateService } from '@ngx-translate/core';
// import { LayoutStore } from '../../../store/layout.store';
// import { UtilService } from '../../../core/services/util.service';
// import { SidebarMenuItem } from '../models/private-sidebar.types';
// import { IaAssistService } from '../../../features/shared/services/ia-assist.service';
// import { LoadingService } from '../../../core/services/loading.service';
// import { GenericDialogService } from '../../../core/services/generic-dialog.service';
// import { HelperDialogComponent } from '../dialogs/helper/helper-dialog.component';
// import { AppSnackbarService } from '../../../core/notifications/snackbar.service';
// import { environment } from '../../../environments/environment';
// import { finalize } from 'rxjs/operators';

// @Component({
//   selector: 'app-private-sidebar',
//   standalone: true,
//   imports: [CommonModule, RouterModule, TranslateModule],
//   templateUrl: './private-sidebar.component.html',
//   styleUrls: ['./private-sidebar.component.scss'],
// })
// export class PrivateSidebarComponent {
//   @Input() menuItems: SidebarMenuItem[] = [];

//   private readonly layoutStore = inject(LayoutStore);
//   private readonly utilService = inject(UtilService);

//   readonly isExpanded = this.layoutStore.isSidebarExpanded;
//   readonly isMobileOpen = this.layoutStore.isMobileSidebarOpen;

//   // Stored IA token (access_token string) — nullable
//   tokenAI: string | null = null;

//   menuId(id: string): string {
//     return 'menu-' + this.utilService.removeAccentuationForID(id);
//   }

//   private readonly iaAssistService = inject(IaAssistService);
//   private readonly loadingService = inject(LoadingService);
//   private readonly genericDialog = inject(GenericDialogService);
//   private readonly translate = inject(TranslateService);
//   private readonly snackbarService = inject(AppSnackbarService);
//   private readonly router = inject(Router);

//   private readonly openSubmenus = signal<Set<string>>(new Set());
//   private readonly openSubSubmenus = signal<Set<string>>(new Set());
//   private readonly openSubSubSubmenus = signal<Set<string>>(new Set());

//   isSubmenuOpen(itemId: string): boolean {
//     return this.openSubmenus().has(itemId);
//   }

//   toggleSubmenu(itemId: string): void {
//     this.openSubmenus.update(set => {
//       const next = new Set(set);
//       if (next.has(itemId)) {
//         next.delete(itemId);
//       } else {
//         next.add(itemId);
//       }
//       return next;
//     });
//   }

//   isSubSubmenuOpen(itemId: string): boolean {
//     return this.openSubSubmenus().has(itemId);
//   }

//   toggleSubSubmenu(itemId: string): void {
//     this.openSubSubmenus.update(set => {
//       const next = new Set(set);
//       if (next.has(itemId)) {
//         next.delete(itemId);
//       } else {
//         next.add(itemId);
//       }
//       return next;
//     });
//   }

//   isSubSubSubmenuOpen(itemId: string): boolean {
//     return this.openSubSubSubmenus().has(itemId);
//   }

//   toggleSubSubSubmenu(itemId: string): void {
//     this.openSubSubSubmenus.update(set => {
//       const next = new Set(set);
//       if (next.has(itemId)) {
//         next.delete(itemId);
//       } else {
//         next.add(itemId);
//       }
//       return next;
//     });
//   }

//   toggleSidebar(): void {
//     this.layoutStore.toggleSidebar();
//   }

//   closeOnMobile(): void {
//     this.layoutStore.closeMobileSidebar();
//   }

//   closeMobileOverlay(): void {
//     this.layoutStore.closeMobileSidebar();
//   }

//   onSaraClick(): void {
//     // Close mobile drawer first
//     this.closeOnMobile();

//     // Feature flag check
//     if (!environment.VIEW_AI) {
//       const helperData = {
//         sections: [
//           {
//             type: 'hero',
//             icon: 'smart_toy',
//             title: 'iaassist.helper.not_available.title',
//             subtitle: 'iaassist.helper.not_available.description',
//             className: 'hero-card',
//           }
//         ],
//         actions: [
//           { label: 'iaassist.helper.ok', icon: 'check', closeOnClick: true }
//         ]
//       } as any;

//       this.genericDialog.open(
//         {
//           title: 'iaassist.helper.not_available.title',
//           icon: 'help_outline',
//           iconTheme: 'primary',
//           buttons: [],
//           showCloseButton: true,
//           contentData: helperData,
//           maxWidth: '560px',
//         },
//         HelperDialogComponent,
//       );

//       return;
//     }
//     // If we already have a cached IA token, try to use it first (no re-login)
//     let cached = localStorage.getItem('token_ia');
//     // Normalize cached token: if it's a JSON string containing access_token, extract it
//     if (cached) {
//       try {
//         const parsed = JSON.parse(cached);
//         if (parsed?.access_token || parsed?.accessToken) {
//           cached = parsed.access_token ?? parsed.accessToken;
//         }
//       } catch {
//         // not JSON, keep as-is
//       }
//     }
//     if (cached) {
//       this.loadingService.show();
//       this.iaAssistService.getInstance(cached)
//         .pipe(finalize(() => this.loadingService.hide()))
//         .subscribe({
//           next: (inst: any) => {
//             // API returns an object { instances: [...] } or legacy array — support both
//             const status = Array.isArray(inst)
//               ? inst?.[0]?.status
//               : inst?.instances?.[0]?.status;
//             const ready = status === 'COMPLETED';
//             if (ready) {
//               this.router.navigate(['/app/iaassist']);
//             } else {
//               const helperData = {
//                 sections: [
//                   { type: 'hero', icon: 'autorenew', title: 'iaassist.helper.syncing.title', subtitle: 'iaassist.helper.syncing.description', className: 'hero-card' }
//                 ],
//                 actions: [ { label: 'iaassist.helper.ok', icon: 'check', closeOnClick: true } ]
//               } as any;

//               this.genericDialog.open({ title: 'iaassist.helper.syncing.title', icon: 'help_outline', contentData: helperData, showCloseButton: true, buttons: [] }, HelperDialogComponent);
//             }
//           },
//           error: (err: any) => {
//             // Do not trigger global logout here. Inform the user and allow manual retry.
//             if (err?.status === 401) {
//               const helperData = {
//                 sections: [
//                   { type: 'hero', icon: 'error', title: 'iaassist.helper.auth_error.title', subtitle: 'iaassist.helper.auth_error.description', className: 'hero-card' }
//                 ],
//                 actions: [ { label: 'iaassist.helper.ok', icon: 'check', closeOnClick: true } ]
//               } as any;

//               this.genericDialog.open({ title: 'iaassist.helper.auth_error.title', icon: 'error', contentData: helperData, showCloseButton: true, buttons: [] }, HelperDialogComponent);
//             } else {
//               this.snackbarService.error(this.translate.instant('errors.http.server_error'));
//             }
//           }
//         });

//       return;
//     }

//     // No cached token — run authentication and then instance check
//     this.loadingService.show();
//     this.iaAssistService.authenticationIa()
//       .pipe(finalize(() => this.loadingService.hide()))
//       .subscribe({
//         next: (resp: any) => {
//           // Support multiple token shapes returned by the API
//           const token = resp?.jsonToken ?? resp?.token ?? resp?.access_token ?? resp?.accessToken ?? resp?.data?.token;
//           if (!token || typeof token !== 'string') {
//             const helperData = {
//               sections: [
//                 { type: 'hero', icon: 'error', title: 'iaassist.helper.auth_error.title', subtitle: 'iaassist.helper.auth_error.description', className: 'hero-card' }
//               ],
//               actions: [ { label: 'iaassist.helper.ok', icon: 'check', closeOnClick: true } ]
//             } as any;

//             this.genericDialog.open({ title: 'iaassist.helper.auth_error.title', icon: 'error', contentData: helperData, showCloseButton: true, buttons: [] }, HelperDialogComponent);
//             return;
//           }

//           // Persist IA token locally BEFORE any other calls so it can be reused independent of system session
//           let tokenToStore = token;
//           // If token appears to be a JSON string, try to parse and extract access_token
//           if (typeof token === 'string') {
//             try {
//               const parsed = JSON.parse(token);
//               if (parsed?.access_token || parsed?.accessToken) {
//                 tokenToStore = parsed.access_token ?? parsed.accessToken;
//               }
//             } catch {
//               // not JSON
//             }
//           }

//           try {
//             localStorage.setItem('token_ia', tokenToStore);
//             this.tokenAI = tokenToStore;
//           } catch (e) {
//             // ignore storage errors but continue
//             console.warn('Unable to persist IA token locally', e);
//             this.tokenAI = tokenToStore;
//           }

//           // validate instance
//           this.loadingService.show();
//           this.iaAssistService.getInstance(tokenToStore)
//             .pipe(finalize(() => this.loadingService.hide()))
//             .subscribe({
//               next: (inst: any) => {
//                 const status = Array.isArray(inst)
//                   ? inst?.[0]?.status
//                   : inst?.instances?.[0]?.status;
//                 const ready = status === 'COMPLETED';
//                 if (ready) {
//                   this.router.navigate(['/app/iaassist']);
//                 } else {
//                   const helperData = {
//                     sections: [
//                       { type: 'hero', icon: 'autorenew', title: 'iaassist.helper.syncing.title', subtitle: 'iaassist.helper.syncing.description', className: 'hero-card' }
//                     ],
//                     actions: [ { label: 'iaassist.helper.ok', icon: 'check', closeOnClick: true } ]
//                   } as any;

//                   this.genericDialog.open({ title: 'iaassist.helper.syncing.title', icon: 'help_outline', contentData: helperData, showCloseButton: true, buttons: [] }, HelperDialogComponent);
//                 }
//               },
//               error: (err: any) => {
//                 // If instance fetch fails, do not trigger global logout here.
//                 if (err?.status === 401) {
//                   const helperData = {
//                     sections: [
//                       { type: 'hero', icon: 'error', title: 'iaassist.helper.auth_error.title', subtitle: 'iaassist.helper.auth_error.description', className: 'hero-card' }
//                     ],
//                     actions: [ { label: 'iaassist.helper.ok', icon: 'check', closeOnClick: true } ]
//                   } as any;

//                   this.genericDialog.open({ title: 'iaassist.helper.auth_error.title', icon: 'error', contentData: helperData, showCloseButton: true, buttons: [] }, HelperDialogComponent);
//                 } else {
//                   this.snackbarService.error(this.translate.instant('errors.http.server_error'));
//                 }
//               }
//             });
//         },
//         error: (err: any) => {
//           if (err?.status === 400) {
//             const helperData = {
//               sections: [
//                 {
//                   type: 'hero',
//                   icon: 'hub',
//                   title: 'iaassist.helper.no_integration.title',
//                   subtitle: 'iaassist.helper.no_integration.description',
//                   className: 'hero-card',
//                 }
//               ],
//               actions: [{ label: 'iaassist.helper.ok', icon: 'check', closeOnClick: true }]
//             } as any;

//             this.genericDialog.open(
//               {
//                 title: 'iaassist.helper.no_integration.title',
//                 icon: 'hub',
//                 iconTheme: 'primary',
//                 buttons: [],
//                 showCloseButton: true,
//                 contentData: helperData,
//                 maxWidth: '560px',
//               },
//               HelperDialogComponent,
//             );
//           } else {
//             const backendMsg = err?.error?.errorMessage ?? err?.error?.message ?? null;
//             const message = backendMsg ?? this.translate.instant('errors.http.server_error');
//             this.snackbarService.error(message);
//           }
//         }
//       });
//   }
// }
